import React, { useState, useEffect, useRef, useMemo } from "react";
import BaseModal from "./BaseModal";
import Button from "../forms/Button";
import useAuth from "../../hooks/useAuth";
import { isAdmin } from "../../utils/permissions";
import { formatDateLocal, parseDateLocal } from "../../utils/dateUtils";
import { axiosPrivate } from "../../api/axios";
import { Calendar, Briefcase, User, ChevronDown, Search } from "lucide-react";

const REQUEST_TYPES = [
  { value: "vacation", label: "Vacation" },
  { value: "mission", label: "Mission" },
  { value: "late_coming", label: "Late Coming" },
  { value: "early_leave", label: "Early Leave" },
];

// Get today's date in YYYY-MM-DD format (local time)
const getTodayDate = () => formatDateLocal(new Date());

// Get tomorrow's date in YYYY-MM-DD format (local time).
// Regular members must submit requests at least one day in advance.
const getTomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return formatDateLocal(d);
};

// High Potit (High Potential) members may request the same day; everyone
// else defaults to (and is limited to) tomorrow or later.
const getDefaultFormData = (isAdmin = false) => ({
  requestType: "",
  isWFH: false,
  vacationType: "", // "sick" | "other" — only used when requestType is "vacation"
  startDate: isAdmin ? getTodayDate() : getTomorrowDate(),
  endDate: isAdmin ? getTodayDate() : getTomorrowDate(),
  employeeId: "",
});

const CreateRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialData = null,
  title: titleProp,
}) => {
  const { employee } = useAuth();
  const isAdminUser = isAdmin(employee);

  const [formData, setFormData] = useState(() => getDefaultFormData(isAdminUser));

  const [errors, setErrors] = useState({});
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState("");
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const employeeDropdownRef = useRef(null);

  // Helper: get vacation balance for current target employee
  const getVacationBalance = () => {
    if (isAdminUser) {
      if (!formData.employeeId) return 0;
      const target = employees.find((emp) => emp._id === formData.employeeId);
      return target?.vacationBalance ?? 0;
    }
    return employee?.vacationBalance ?? 0;
  };

  const vacationBalance = getVacationBalance();
  const canRequestVacation = vacationBalance > 0;

  // The advance-notice rule (must be at least one day ahead, no same-day/past)
  // applies only to WFH/missions and "Other" vacation. Exempt: sick vacation,
  // late coming, early leave, and High Potit (High Potential) members.
  const isSickVacation =
    formData.requestType === "vacation" && formData.vacationType === "sick";
  const isAdvanceNoticeExempt =
    isSickVacation ||
    formData.requestType === "late_coming" ||
    formData.requestType === "early_leave";
  const requiresAdvanceNotice = !isAdminUser && !isAdvanceNoticeExempt;

  // Max end date for vacation: start + (balance - 1) days (inclusive duration = balance days)
  const getMaxEndDateForVacation = () => {
    if (formData.requestType !== "vacation" || !formData.startDate || vacationBalance <= 0)
      return null;
    const start = parseDateLocal(formData.startDate);
    if (isNaN(start.getTime())) return null;
    const maxEnd = new Date(start);
    maxEnd.setDate(start.getDate() + (vacationBalance - 1));
    return formatDateLocal(maxEnd);
  };
  const maxEndDateForVacation = getMaxEndDateForVacation();

  // Clamp end date to vacation balance when start date, employee, or type changes
  useEffect(() => {
    if (
      formData.requestType !== "vacation" ||
      !formData.startDate ||
      !formData.endDate ||
      vacationBalance <= 0 ||
      !maxEndDateForVacation
    )
      return;
    if (formData.endDate > maxEndDateForVacation) {
      setFormData((prev) => ({ ...prev, endDate: maxEndDateForVacation }));
    }
  }, [
    formData.requestType,
    formData.startDate,
    formData.endDate,
    formData.employeeId,
    vacationBalance,
    maxEndDateForVacation,
  ]);

  // Pre-fill form only when modal opens (avoid overwriting while user types)
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      prevOpenRef.current = false;
      return;
    }
    const justOpened = !prevOpenRef.current;
    prevOpenRef.current = true;
    if (!justOpened) return;

    if (initialData && typeof initialData === "object") {
      const startDate =
        typeof initialData.startDate === "string"
          ? initialData.startDate.includes("T")
            ? formatDateLocal(new Date(initialData.startDate))
            : initialData.startDate
          : getTodayDate();
      const endDate =
        typeof initialData.endDate === "string"
          ? initialData.endDate.includes("T")
            ? formatDateLocal(new Date(initialData.endDate))
            : initialData.endDate
          : getTodayDate();
      setFormData({
        requestType: initialData.requestType || "",
        isWFH: Boolean(initialData.isWFH),
        vacationType: initialData.vacationType || "",
        startDate,
        endDate,
        employeeId: initialData.employeeId || "",
      });
    } else {
      setFormData(getDefaultFormData(isAdminUser));
    }
  }, [isOpen, initialData, isAdminUser]);

  // Fetch employees if admin
  useEffect(() => {
    if (isAdminUser && isOpen) {
      fetchEmployees();
    }
  }, [isAdminUser, isOpen]);

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await axiosPrivate.get("/api/employees");

      console.log("Employees response:", response);
      console.log("Response data:", response.data);

      // Handle different response structures
      let employeesData = [];

      if (Array.isArray(response.data)) {
        employeesData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        employeesData = response.data.data;
      } else if (
        response.data?.employees &&
        Array.isArray(response.data.employees)
      ) {
        employeesData = response.data.employees;
      }

      console.log("Processed employees data:", employeesData);
      setEmployees(employeesData);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  // Filter employees by search query (name, email, employeeCode, id)
  const filteredEmployees = useMemo(() => {
    const q = (employeeSearchQuery || "").trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((emp) => {
      const nameEn = (emp.fullNameEn || "").toLowerCase();
      const nameAr = (emp.fullNameAr || "").toLowerCase();
      const email = (emp.email || "").toLowerCase();
      const code = (emp.employeeCode || "").toLowerCase();
      const id = (emp._id || emp.id || "").toString().toLowerCase();
      return (
        nameEn.includes(q) ||
        nameAr.includes(q) ||
        email.includes(q) ||
        code.includes(q) ||
        id.includes(q)
      );
    });
  }, [employees, employeeSearchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        employeeDropdownRef.current &&
        !employeeDropdownRef.current.contains(e.target)
      ) {
        setShowEmployeeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Reset search when dropdown closes or modal closes
  useEffect(() => {
    if (!showEmployeeDropdown) setEmployeeSearchQuery("");
  }, [showEmployeeDropdown]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Require employee only when creating (not when editing with initialData)
    if (isAdminUser && !formData.employeeId && !initialData) {
      newErrors.employeeId = "Employee selection is required";
    }

    if (!formData.requestType) {
      newErrors.requestType = "Request type is required";
    }

    // Prevent creating vacation requests when balance is finished
    if (formData.requestType === "vacation" && !canRequestVacation) {
      newErrors.requestType =
        "Your vacation balance is finished. You cannot create a vacation request.";
    }

    // Vacation requires a sub-type: Sick or Other.
    if (formData.requestType === "vacation" && !formData.vacationType) {
      newErrors.vacationType = "Please select a vacation type";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    // Requests must be submitted at least one day in advance: same-day and
    // past dates are not allowed. Sick vacation and High Potit (High
    // Potential) members are exempt. Only applies when creating (not editing).
    if (
      requiresAdvanceNotice &&
      !initialData &&
      formData.startDate &&
      formData.startDate <= getTodayDate()
    ) {
      newErrors.startDate =
        "Requests must be submitted at least one day in advance. You cannot request today or a past date.";
    }

    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);

      if (start > end) {
        newErrors.endDate = "End date must be after or equal to start date";
      }

      // Vacation: duration must not exceed employee's balance
      if (formData.requestType === "vacation" && canRequestVacation) {
        const diffTime = Math.abs(end - start);
        const durationDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (durationDays > vacationBalance) {
          newErrors.endDate = `Vacation duration cannot exceed ${vacationBalance} day${vacationBalance === 1 ? "" : "s"} (available balance). Max end date is ${maxEndDateForVacation || ""}.`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      return;
    }

    await onSubmit(formData);

    // Reset form
    setFormData(getDefaultFormData(isAdminUser));
    setErrors({});
  };

  const handleClose = () => {
    setFormData(getDefaultFormData(isAdminUser));
    setErrors({});
    setShowEmployeeDropdown(false);
    setEmployeeSearchQuery("");
    onClose();
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      title={titleProp ?? (initialData ? "Edit Request" : "Create Request")}
      subtitle={initialData ? "Update the request details" : "Submit a new request for approval"}
      size="md"
      footer={
        <>
          <Button
            variant="bordered"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? initialData
                ? "Updating..."
                : "Submitting..."
              : initialData
                ? "Update Request"
                : "Submit Request"}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Employee Selector - Only for Admin (searchable) */}
        {isAdminUser && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Select Employee
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div ref={employeeDropdownRef} className="relative">
              <button
                type="button"
                onClick={() =>
                  !loadingEmployees && setShowEmployeeDropdown((prev) => !prev)
                }
                disabled={loadingEmployees}
                className={`w-full flex items-center gap-2 pl-11 pr-10 py-3 bg-white dark:bg-dark-100 border ${
                  errors.employeeId
                    ? "border-red-500"
                    : "border-gray-300 dark:border-dark-200"
                } rounded-lg text-left text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 shrink-0" />
                <span className="truncate">
                  {formData.employeeId
                    ? (() => {
                        const emp = employees.find(
                          (e) => (e._id || e.id) === formData.employeeId
                        );
                        return emp
                          ? `${emp.fullNameEn || emp.fullNameAr || "Unknown"} - ${emp.employeeCode || ""}`
                          : "Select employee";
                      })()
                    : "Select employee"}
                </span>
                <ChevronDown
                  className={`absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 shrink-0 transition-transform ${
                    showEmployeeDropdown ? "rotate-180" : ""
                  }`}
                />
              </button>

              {showEmployeeDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-dark-100 border border-gray-300 dark:border-dark-200 rounded-lg shadow-lg z-30 overflow-hidden">
                  <div className="p-2 border-b border-gray-200 dark:border-dark-200">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={employeeSearchQuery}
                        onChange={(e) =>
                          setEmployeeSearchQuery(e.target.value)
                        }
                        placeholder="Search by name, email, or ID..."
                        className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-dark-200 border border-gray-200 dark:border-dark-200 rounded-md text-gray-900 dark:text-light-100 placeholder-gray-500 focus:ring-2 focus:ring-primary-accent focus:border-transparent"
                        autoFocus
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {loadingEmployees ? (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-light-500">
                        Loading employees...
                      </div>
                    ) : filteredEmployees.length === 0 ? (
                      <div className="p-4 text-center text-sm text-gray-500 dark:text-light-500">
                        {employeeSearchQuery.trim()
                          ? "No employees match your search"
                          : "No employees found"}
                      </div>
                    ) : (
                      filteredEmployees.map((emp) => {
                        const empId = emp._id || emp.id;
                        const isSelected =
                          String(formData.employeeId) === String(empId);
                        return (
                          <button
                            key={empId}
                            type="button"
                            onClick={() => {
                              handleChange("employeeId", empId);
                              setShowEmployeeDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-dark-200 transition-colors ${
                              isSelected
                                ? "bg-primary-accent/10 text-primary-accent font-medium"
                                : ""
                            }`}
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-light-100">
                              {emp.fullNameEn || emp.fullNameAr || "Unknown"}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-light-500">
                              {emp.employeeCode || ""}
                              {emp.email ? ` • ${emp.email}` : ""}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
            {errors.employeeId && (
              <p className="mt-1 text-sm text-red-500">{errors.employeeId}</p>
            )}
          </div>
        )}

        {/* Request Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
            Request Type
            <span className="text-red-500 ml-1">*</span>
          </label>
          <div role="radiogroup" aria-label="Request type" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {REQUEST_TYPES.map((type) => {
              const disabled = type.value === "vacation" && !canRequestVacation;
              const active = formData.requestType === type.value;
              return (
                <button
                  key={type.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  disabled={disabled}
                  title={disabled ? "No vacation balance" : undefined}
                  onClick={() => {
                    const value = type.value;
                    setFormData((prev) => ({
                      ...prev,
                      requestType: value,
                      // Reset vacation sub-type when leaving the vacation type
                      vacationType: value === "vacation" ? prev.vacationType : "",
                    }));
                    if (errors.requestType) {
                      setErrors((prev) => ({ ...prev, requestType: null }));
                    }
                  }}
                  className={`h-10 px-2 rounded-xl border text-[13px] font-medium whitespace-nowrap transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    active
                      ? "bg-accent-soft border-accent-line text-accent-ink"
                      : `bg-panel border-line2 text-muted hover:text-ink hover:bg-hover ${errors.requestType ? "border-danger/50" : ""}`
                  }`}
                >
                  {type.label}
                  {disabled ? " (no balance)" : ""}
                </button>
              );
            })}
          </div>
          {errors.requestType && (
            <p className="mt-1 text-sm text-red-500">{errors.requestType}</p>
          )}

          {/* Vacation balance info (only when relevant) */}
          {formData.requestType === "vacation" && (
            <p className="mt-2 text-xs text-gray-500 dark:text-light-400">
              Vacation balance:{" "}
              <span className="font-semibold">
                {vacationBalance ?? 0} day{vacationBalance === 1 ? "" : "s"}
              </span>
            </p>
          )}
        </div>

        {/* Vacation Type - Only for Vacation */}
        {formData.requestType === "vacation" && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Vacation Type
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <Briefcase className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                value={formData.vacationType}
                onChange={(e) => handleChange("vacationType", e.target.value)}
                className={`w-full pl-11 pr-4 py-3 bg-white dark:bg-dark-100 border ${
                  errors.vacationType
                    ? "border-red-500"
                    : "border-gray-300 dark:border-dark-200"
                } rounded-lg text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all appearance-none cursor-pointer`}
              >
                <option value="">Select vacation type</option>
                <option value="sick">Sick</option>
                <option value="other">Other</option>
              </select>
            </div>
            {errors.vacationType && (
              <p className="mt-1 text-sm text-red-500">{errors.vacationType}</p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-light-400">
              {formData.vacationType === "sick"
                ? "Sick vacation can be requested for the same day."
                : "Other vacation must be requested at least one day in advance."}
            </p>
          </div>
        )}

        {/* WFH Toggle - Only for Mission */}
        {formData.requestType === "mission" && (
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isWFH}
                onChange={(e) => handleChange("isWFH", e.target.checked)}
                className="w-5 h-5 text-primary-accent bg-gray-100 border-gray-300 rounded focus:ring-primary-accent focus:ring-2"
              />
              <div>
                <span className="text-sm font-semibold text-gray-900 dark:text-light-100">
                  Work From Home (WFH)
                </span>
                <p className="text-xs text-gray-600 dark:text-light-400 mt-0.5">
                  Check this if the mission will be done remotely
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Date Range */}
        <div className="grid grid-cols-2 gap-4">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              Start Date
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleChange("startDate", e.target.value)}
                min={
                  requiresAdvanceNotice && !initialData
                    ? getTomorrowDate()
                    : undefined
                }
                className={`w-full pl-11 pr-4 py-3 bg-white dark:bg-dark-100 border ${
                  errors.startDate
                    ? "border-red-500"
                    : "border-gray-300 dark:border-dark-200"
                } rounded-lg text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all`}
              />
            </div>
            {errors.startDate && (
              <p className="mt-1 text-sm text-red-500">{errors.startDate}</p>
            )}
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-light-200 mb-2">
              End Date
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => handleChange("endDate", e.target.value)}
                min={formData.startDate}
                max={
                  formData.requestType === "vacation" && maxEndDateForVacation
                    ? maxEndDateForVacation
                    : undefined
                }
                className={`w-full pl-11 pr-4 py-3 bg-white dark:bg-dark-100 border ${
                  errors.endDate
                    ? "border-red-500"
                    : "border-gray-300 dark:border-dark-200"
                } rounded-lg text-gray-900 dark:text-light-100 focus:ring-2 focus:ring-primary-accent focus:border-transparent transition-all`}
              />
            </div>
            {errors.endDate && (
              <p className="mt-1 text-sm text-red-500">{errors.endDate}</p>
            )}
            {formData.requestType === "vacation" &&
              vacationBalance > 0 &&
              maxEndDateForVacation && (
                <p className="mt-1.5 text-xs text-gray-500 dark:text-light-400">
                  Max end date: {maxEndDateForVacation} ({vacationBalance} day
                  {vacationBalance === 1 ? "" : "s"} available)
                </p>
              )}
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default CreateRequestModal;
